/**
 * Session context: `/v1/app/me` loaded once, then `can()`, `entitled()`, `flag()` everywhere.
 * UI gating is a convenience only — the server enforces every permission (scenario S1.7).
 */
import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { hasPermission } from '@shared/permissions.mjs';
import { ApiError, setCsrf, setSecondStepHandler, setUnauthorizedHandler } from './http';
import { stepOf } from './secondStep';
import { setTenantTz } from './format';
import { useData } from './provider';
import type { Me } from './types';

interface SessionCtx {
  me: Me;
  can: (perm: string | string[]) => boolean;
  entitled: (product: string) => boolean;
  flag: (key: string) => boolean;
  tz: string;
}
const Ctx = createContext<SessionCtx | null>(null);

/** Product key → module gate. A module is on when any of its keys (or a bundle containing it) is entitled. */
export const MODULE_ENTITLEMENT: Record<string, string[]> = {
  leads: ['lead_desk', 'ai_receptionist', 'front_office_ai'],
  dispatch: ['dispatch_ai', 'mobile_ops'],
  parts: ['parts', 'parts_inventory'],
  analytics: ['analytics', 'command_center'],
  fleet: ['fleet_portal', 'fleet_os'],
  passport: ['service_passport', 'passport'],
};

export const safeNext = (n: string | null) => (n && n.startsWith('/') && !n.startsWith('//') ? n : '/today');
/** The second sign-in step's screen (shell/SecondStep.tsx), keeping where the person was going. */
export const stepUrl = (next: string) => `/login/step?next=${encodeURIComponent(next)}`;

export function SessionRoot({ children, loading, failed }: { children: ReactNode; loading: ReactNode; failed: (e: unknown, retry: () => void) => ReactNode }) {
  const { data } = useData();
  const nav = useNavigate();
  const loc = useLocation();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['me'], queryFn: () => data.session.me(), staleTime: 5 * 60_000, retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2 });

  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (location.pathname.startsWith('/login')) return;
      qc.clear();
      nav(`/login?next=${encodeURIComponent(location.pathname + location.search)}`, { replace: true });
    });
    // A request refused because this session owes the second sign-in step (the store began to require it, or the
    // account set it up elsewhere): leave the app for the step's screen and come back to the same place afterwards.
    setSecondStepHandler(() => {
      if (location.pathname.startsWith('/login') || location.pathname.startsWith('/outreach')) return;
      qc.clear();
      nav(stepUrl(location.pathname + location.search), { replace: true });
    });
  }, [nav, qc]);

  useEffect(() => {
    if (q.data) { setCsrf(q.data.csrf); setTenantTz(q.data.tenant.timezone); }
  }, [q.data]);

  if (q.error instanceof ApiError && q.error.status === 401) {
    if (!loc.pathname.startsWith('/login')) nav(`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`, { replace: true });
    return null;
  }
  // A failed refetch (a 429 while the tab regains focus, a blip) keeps the session it already has: only a first load
  // that failed shows the failure screen.
  if (!q.data) return <>{q.error ? failed(q.error, () => q.refetch()) : loading}</>;
  // A session that has passed the password but not the second step sees no app screen: not on a reload, not by a deep
  // link. (The server refuses it everything but /me and the step's own routes; this keeps the screens from even drawing.)
  if (stepOf(q.data.mfa) !== 'none') return <Navigate to={stepUrl(loc.pathname + loc.search)} replace />;
  const me = q.data;
  const principal = { role: me.user.role, perms: me.permissions };
  const ent = new Set(me.entitlements);
  const value: SessionCtx = {
    me,
    tz: me.tenant.timezone,
    can: (p) => hasPermission(principal, p),
    entitled: (mod) => { const keys = MODULE_ENTITLEMENT[mod]; return !keys || keys.some((k) => ent.has(k)) || ent.has(mod); },
    flag: (k) => !!me.flags[k],
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession() outside <SessionRoot>');
  return v;
}
